import * as VideoProcessors from '@twilio/video-processors';
import {BackgroundProcessor} from '@twilio/video-processors/es5/processors/background/BackgroundProcessor';
import cx from 'classnames';
import {forEach, isEqual, min} from 'lodash';
import {PureComponent, createRef} from 'react';
import Draggable from 'react-draggable';
import {withTranslation, WithTranslation} from 'react-i18next';
import Video,
{LocalAudioTrack, LocalAudioTrackPublication, LocalVideoTrack, LocalVideoTrackPublication, Room} from 'twilio-video';

import {Loader, ResizableBoxWrapper} from 'components';
import {LOCAL_MEDIA_ID, REMOTE_MEDIA_ID, SCREEN_SHARING_VIDEO_TRACK, VIDEO_PIN_AUTO_HIDE_TIMEOUT} from 'constants/session';
import {themeConfig} from 'constants/theme';
import {VideoPinParticipant, VirtualBackgroundType} from 'enums';
import {MediaDevicesConstraints, VirtualBackgroundConfig} from 'types';
import './styles.scss';
import {logger} from 'utils';

const miniConstrains = {
    // [width, height]
    minConstraints: [220, 165] as [number, number],
    maxConstraints: [480, 360] as [number, number]
};

type State = {
    readonly parentHeight: number;
    readonly remoteVideoHeight: number;
    readonly parentWidth: number;
    readonly remoteVideoWidth: number;
    readonly localVideoOverlayVisible: boolean;
    readonly localVideoOverlayTimeout: ReturnType<typeof setTimeout>;
    readonly remoteVideoOverlayVisible: boolean;
    readonly remoteVideoOverlayTimeout: ReturnType<typeof setTimeout>;
    readonly trackSubscribeChangeDate: Date; // updated after new track published event to trigger re-render
    readonly backgroundProcessor: BackgroundProcessor;
}

const initialState: State = {
    parentHeight: null,
    remoteVideoHeight: null,
    parentWidth: null,
    remoteVideoWidth: null,
    localVideoOverlayVisible: false,
    localVideoOverlayTimeout: null,
    remoteVideoOverlayVisible: false,
    remoteVideoOverlayTimeout: null,
    trackSubscribeChangeDate: null,
    backgroundProcessor: null,
};

type Props = {
    room: Room;
    pinParticipant?: VideoPinParticipant;
    mediaConstraints?: MediaDevicesConstraints;
    isFullScreen?: boolean;
    skipAutoDetectFullScreen?: boolean;
    isAudioEnabled?: boolean;
    isVideoEnabled?: boolean;
    mini?: boolean;
    miniPositionClassName?: string;
    overLocalVideoContent?: React.ReactNode,
    overRemoteVideoContent?: React.ReactNode,
    parentRect?: {
        width: number,
        height: number
    };
    virtualBackgroundConfig?: VirtualBackgroundConfig;
    toggleFullScreen?: (value: boolean) => void;
    onLocalNetworkQualityLevelChange?: (level: number) => void;
    onRemoteNetworkQualityLevelChange?: (level: number) => void;
} & WithTranslation;

class TwilioVideo extends PureComponent<Props, State> {

    readonly state = initialState;
    static readonly defaultProps = {
        isAudioEnabled: true,
        isVideoEnabled: true,
        miniPositionClassName: 'tw-absolute tw-top-4 tw-left-4'
    };

    localMediaRef = createRef<HTMLDivElement>();
    remoteMediaRef = createRef<HTMLDivElement>();
    componentRef = createRef<HTMLDivElement>();

    _isMounted = false;

    componentDidMount() {
        this._isMounted = true;
        window.addEventListener('resize', this.updateDimensions);

        const {localMediaRef, remoteMediaRef} = this;
        const {skipAutoDetectFullScreen} = this.props;

        const localMediaEl = localMediaRef.current as HTMLElement;
        const remoteMediaEl = remoteMediaRef.current as HTMLElement;

        if (!localMediaEl || !remoteMediaEl) {
            return;
        }

        if (!localMediaEl.querySelector('video')) {
            this.participantConnected(this.props.room.localParticipant, true, localMediaEl);
        }

        this.subscribeRoomEvents(remoteMediaEl);
        this.toggleAudio();
        this.toggleVideo();

        setTimeout(() => {
            this.updateDimensions();
            if (!skipAutoDetectFullScreen) {
                this.setDefaultFullScreen();
            }
        });
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.mini !== prevProps.mini) {
            this.updateDimensions();
        }
        if (this.props.isAudioEnabled !== prevProps.isAudioEnabled) {
            this.toggleAudio();
        }
        if (this.props.isVideoEnabled !== prevProps.isVideoEnabled) {
            this.toggleVideo();
        }
        if (this.props.isFullScreen !== prevProps.isFullScreen) {
            this.updateDimensions();
        }
        if (this.props.mediaConstraints) {
            if (!isEqual(this.props.mediaConstraints?.videoinput, prevProps.mediaConstraints?.videoinput)) {
                this.updateVideoDevice(this.props.mediaConstraints?.videoinput);
            }
            if (prevProps.mediaConstraints?.audiooutput !== this.props.mediaConstraints?.audiooutput) {
                this.updateAudioOutputDevice(this.props.mediaConstraints.audiooutput);
            }
            if (!isEqual(prevProps.mediaConstraints?.audioinput, this.props.mediaConstraints?.audioinput)) {
                this.updateAudioInputDevice(this.props.mediaConstraints.audioinput as MediaTrackConstraintSet);
            }
        }
        if (this.props.parentRect?.width !== prevProps.parentRect?.width ||
            this.props.parentRect?.height !== prevProps.parentRect?.height) {
            setTimeout(() => {
                this.updateDimensions();
            });
        }
        if (this.props.virtualBackgroundConfig !== prevProps.virtualBackgroundConfig) {
            this.setVirtualBackground();
        }
    }

    componentWillUnmount() {
        this.cleanupParticipantTracks(this.props.room.localParticipant);
        window.removeEventListener('resize', this.updateDimensions);
        this._isMounted = false;
    }

    handleParticipantTrack = (publication, container: HTMLElement, isLocal: boolean) => {
        this.publishTrack(publication, container);
        if (!isLocal) {
            const audioElements = container?.getElementsByTagName('audio');
            forEach(Array.from(audioElements), (audioEl: any) => audioEl?.setSinkId?.(this.props.mediaConstraints?.audiooutput));
        }
    };

    participantConnected = (participant, isLocal: boolean, container: HTMLElement) => {
        participant.on('trackPublished', (publication) => {
            this.handleParticipantTrack(publication, container, isLocal);
        });

        participant.on('trackUnpublished', (publication) => {
            if (publication.trackName === SCREEN_SHARING_VIDEO_TRACK) {
                return;
            }
            if (this._isMounted) {
                // eslint-disable-next-line react/no-unused-state
                this.setState({trackSubscribeChangeDate: new Date()});
            }
            if (publication.track) {
                this.detachTrack(publication.track);
            } else if (!publication.isSubscribed) {
                // TODO: remove track without a track
                container.querySelector(publication.kind)?.remove();
            }
        });

        if (this._isMounted) {
            if (isLocal) {
                this.props.onLocalNetworkQualityLevelChange?.(participant.networkQualityLevel);
            } else {
                this.props.onRemoteNetworkQualityLevelChange?.(participant.networkQualityLevel);
            }
        }

        participant.on('trackPublicationFailed', (error, localTrack) => {
            logger.warn({category: logger.category.Twilio, message: `Failed to publish LocalTrack ${localTrack.name}: ${error.message}`});
        });

        participant.on('networkQualityLevelChanged', (level) => {
            if (this._isMounted) {
                if (isLocal) {
                    this.props.onLocalNetworkQualityLevelChange?.(level);
                } else {
                    this.props.onRemoteNetworkQualityLevelChange?.(level);
                }
            }
        });

        participant.tracks.forEach((publication) => {
            this.handleParticipantTrack(publication, container, isLocal);
        });
    };

    publishTrack = (publication, container) => {
        if (publication.trackName === SCREEN_SHARING_VIDEO_TRACK) {
            return;
        }
        if (this._isMounted) {
            // eslint-disable-next-line react/no-unused-state
            this.setState({trackSubscribeChangeDate: new Date()});
        }
        if (publication.track) {
            this.attachTrack(container, publication.track);
        } else if (!publication.isSubscribed) {
            publication.on('subscribed', (track) => {
                this.attachTrack(container, track);
            });
            publication.on('unsubscribed', this.detachTrack);
        }
    };

    attachTrack = (container: HTMLDivElement, track: LocalVideoTrack | LocalAudioTrack) => {
        container.appendChild(track.attach());
        // eslint-disable-next-line react/no-unused-state
        track.on('enabled', () => this.setState({trackSubscribeChangeDate: new Date()}));
        // eslint-disable-next-line react/no-unused-state
        track.on('disabled', () => this.setState({trackSubscribeChangeDate: new Date()}));
    };

    detachTrack = (track) => {
        track.mediaStreamTrack.enabled = false;
        track.detach().forEach((element) => {
            element.remove();
            element.srcObject = null;
        });
    };

    setDefaultFullScreen = (): void => {
        const rect = this.getClientRect();
        if (rect) {
            const {width: rectWidth, height: rectHeight} = rect;

            const initialFullScreen = rectWidth > rectHeight ? false : true;
            this.props.toggleFullScreen?.(initialFullScreen);
        }
    };

    subscribeRoomEvents = (remoteMediaEl: HTMLElement) => {
        const {room} = this.props;

        room.participants.forEach((participant) => {
            this.participantConnected(participant, false, remoteMediaEl);
        });

        room.on('trackSubscriptionFailed', (error, remoteTrackPublication, remoteParticipant) => {
            logger.warn({
                category: logger.category.Twilio,
                message: `Failed to subscribe to RemoteTrack ${remoteTrackPublication.trackName}` +
                ` from RemoteParticipant ${remoteParticipant.identity}: ${error.message}`
            });
        });

        room.on('participantConnected', (participant) => {
            this.participantConnected(participant, false, remoteMediaEl);
        });

        // Detach all participant’s track when they leave a room.
        room.on('participantDisconnected', this.cleanupParticipantTracks);

        room.on('disconnected', () => {
            this.cleanupParticipantTracks(room.localParticipant);
            room.participants.forEach(this.cleanupParticipantTracks);
        });
    };

    private getClientRect = () => {
        const refToSize = this.componentRef;

        if (!refToSize || !refToSize.current) {
            return null;
        }

        const parent: HTMLElement | null = (refToSize.current as HTMLElement).parentElement;
        if (!parent) {
            return null;
        }

        return parent.getBoundingClientRect();
    };

    updateDimensions = () => {
        const refToSize = this.componentRef;
        if (!refToSize || !refToSize.current) {
            return;
        }

        const rect = this.getClientRect();
        if (!rect) {
            return;
        }

        const {width: rectWidth, height: rectHeight} = rect;
        const {isFullScreen, parentRect, pinParticipant} = this.props;

        let width: number;
        let height: number;

        if (rectWidth > rectHeight) {
            width = rectWidth;
            height = width * 3 / 4;
            if (!isFullScreen && height > rectHeight) {
                height = rectHeight;
                width = height / 3 * 4;
            }

            refToSize.current.style.width = isFullScreen ? '100%' : width + 'px';
            (refToSize.current as HTMLElement).style.height = (height < rectHeight ? height : rectHeight) + 'px';
        } else {
            height = rectHeight;
            width = height / 3 * 4;
            if (!isFullScreen && width > rectWidth) {
                width = rectWidth;
                height = width * 3 / 4;
                // workaround for column positioned base medias
                if (!pinParticipant && rectWidth < themeConfig.screens.md) {
                    height = 2 * height;
                }
            }

            refToSize.current.style.height = isFullScreen ? '100%' : height + 'px';
            (refToSize.current as HTMLElement).style.width = width + 'px';
        }

        const minWidth = miniConstrains.minConstraints[0];
        const minHeight = miniConstrains.minConstraints[1];
        if (this.props.mini && (width > minWidth || height > minHeight)) {
            height = minHeight;
            width = minWidth;
        }

        const parentHeight = parentRect ? parentRect.height : window.innerHeight;
        const parentWidth = parentRect ? parentRect.width : window.innerWidth;

        const remoteVideoHeight = height;
        const remoteVideoWidth = width;

        this.setState({parentHeight, remoteVideoHeight, parentWidth, remoteVideoWidth});
    };

    cleanupParticipantTracks = (participant) => {
        const publicationTracks = Array.from(participant.tracks.values()) as any[];

        publicationTracks.forEach((publication) => {
            if (publication.trackName === SCREEN_SHARING_VIDEO_TRACK) {
                return;
            }
            if (publication.track) {
                publication.track.stop();
                participant.unpublishTrack(publication.track);
                this.detachTrack(publication.track);
            }
        });
    };

    updateAudioInputDevice = (props: Video.CreateLocalTrackOptions) => {
        this.props.room.localParticipant.audioTracks.forEach((publication) => {
            publication.track.restart(props);
        });
    };

    updateAudioOutputDevice = (newDeviceId: string) => {
        const audioElements = this.remoteMediaRef.current?.getElementsByTagName('audio');
        forEach(Array.from(audioElements), (audioEl: any) => audioEl?.setSinkId?.(newDeviceId));
    };

    updateVideoDevice = (props: Video.CreateLocalTrackOptions) => {
        this.props.room.localParticipant.videoTracks.forEach((publication) => {
            if (publication.trackName !== SCREEN_SHARING_VIDEO_TRACK) {
                publication.track.restart(props);
            }
        });
    };

    toggleAudio = () => {
        const {isAudioEnabled, room} = this.props;
        this.toggleLocalMedia(isAudioEnabled, room.localParticipant.audioTracks);
    };

    toggleVideo = () => {
        const {isVideoEnabled, room, virtualBackgroundConfig} = this.props;
        this.toggleLocalMedia(isVideoEnabled, room.localParticipant.videoTracks);

        if (isVideoEnabled && virtualBackgroundConfig) {
            this.setVirtualBackground();
        } else if (!isVideoEnabled) {
            this.removeVirtualBackground();
        }
    };

    toggleLocalMedia = (isEnabled: boolean, tracks) => {
        const mediaTracksPublications: (LocalVideoTrackPublication | LocalAudioTrackPublication)[] =
            Array.from(tracks.values());

        mediaTracksPublications
            .map((publicationTrack) => publicationTrack.track)
            .filter((track) => track.name !== SCREEN_SHARING_VIDEO_TRACK)
            .forEach((track) => {
                if (isEnabled) {
                    track.enable();
                } else {
                    track.disable();
                }
            });
    };

    toggleFullScreen = () => {
        setTimeout(this.updateDimensions); // TODO: on props change??
    };

    getVirtualBackgroundProcessor() {
        return new Promise<BackgroundProcessor>((resolve) => {
            const {type, image} = this.props.virtualBackgroundConfig;
            if (type === VirtualBackgroundType.Blur) {
                resolve(new VideoProcessors.GaussianBlurBackgroundProcessor({
                    assetsPath: '/assets/twilio',
                    maskBlurRadius: 10,
                    blurFilterRadius: 5,
                }));
            } else if (type === VirtualBackgroundType.Image && image) {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.src = image;
                img.onload = () => {
                    resolve(new VideoProcessors.VirtualBackgroundProcessor({
                        assetsPath: '/assets/twilio',
                        backgroundImage: img,
                        maskBlurRadius: 5,
                        fitType: VideoProcessors.ImageFit.Fill
                    }));
                };
                img.onerror = () => {
                    logger.warn({
                        category: logger.category.Twilio, message: `Failed to load virtual background image: ${image}`
                    });
                    resolve(null);
                };
            } else if (type === VirtualBackgroundType.None && this.state.backgroundProcessor) {
                resolve(null);
            }
        });

    }

    removeVirtualBackground() {
        if (this.state.backgroundProcessor) {
            this.props.room.localParticipant.videoTracks.forEach((publication) => {
                if (publication.track.kind === 'video' && publication.trackName !== SCREEN_SHARING_VIDEO_TRACK) {
                    publication.track.removeProcessor(this.state.backgroundProcessor);
                }
            });
            this.setState({backgroundProcessor: null});
        }
    }

    async setVirtualBackground() {
        const processor = await this.getVirtualBackgroundProcessor();
        this.props.room.localParticipant.videoTracks.forEach(async (publication) => {
            if (publication.track.kind === 'video' && publication.trackName !== SCREEN_SHARING_VIDEO_TRACK) {
                if (this.state.backgroundProcessor) {
                    publication.track.removeProcessor(this.state.backgroundProcessor);
                }
                if (processor) {
                    await processor.loadModel();
                    publication.track.addProcessor(processor, {
                        inputFrameBufferType: 'video',
                        outputFrameBufferContextType: 'webgl2',
                    });
                }
            }
        });
        this.setState({backgroundProcessor: processor});
    }

    remoteVideoEnabled = () => {
        let enabled = false;
        const {room} = this.props;

        room?.participants.forEach((p) => {
            p.videoTracks.forEach((videoTracks) => {
                if (videoTracks.isTrackEnabled && !enabled) {
                    enabled = true;
                }
            });
        });

        return enabled;
    };

    handleMouseMoveLocalVideo = () => {
        if (this.state.localVideoOverlayTimeout) {
            clearTimeout(this.state.localVideoOverlayTimeout);
        }
        const timeout = setTimeout(() => {
            if (this._isMounted) {
                this.setState({localVideoOverlayVisible: false});
            }
        }, VIDEO_PIN_AUTO_HIDE_TIMEOUT);
        this.setState({localVideoOverlayVisible: true, localVideoOverlayTimeout: timeout});
    };

    handleMouseOutLocalVideo = () => {
        if (this.state.localVideoOverlayTimeout) {
            clearTimeout(this.state.localVideoOverlayTimeout);
        }
        this.setState({localVideoOverlayVisible: false, localVideoOverlayTimeout: null});
    };

    handleMouseMoveRemoteVideo = () => {
        if (this.state.remoteVideoOverlayTimeout) {
            clearTimeout(this.state.remoteVideoOverlayTimeout);
        }
        const timeout = setTimeout(() => {
            if (this._isMounted) {
                this.setState({remoteVideoOverlayVisible: false});
            }
        }, VIDEO_PIN_AUTO_HIDE_TIMEOUT);
        this.setState({remoteVideoOverlayVisible: true, remoteVideoOverlayTimeout: timeout});
    };

    handleMouseOutRemoteVideo = () => {
        if (this.state.remoteVideoOverlayTimeout) {
            clearTimeout(this.state.remoteVideoOverlayTimeout);
        }
        this.setState({remoteVideoOverlayVisible: false, remoteVideoOverlayTimeout: null});
    };

    render() {
        const {
            localMediaRef, remoteMediaRef, componentRef,
            remoteVideoEnabled, handleMouseMoveLocalVideo, handleMouseMoveRemoteVideo,
            handleMouseOutLocalVideo, handleMouseOutRemoteVideo,
            state: {remoteVideoHeight, parentHeight, remoteVideoWidth, parentWidth, localVideoOverlayVisible, remoteVideoOverlayVisible},
            props: {
                mini, miniPositionClassName, room, pinParticipant,
                isFullScreen, overLocalVideoContent, overRemoteVideoContent
            }
        } = this;

        const isLandscape = parentHeight < parentWidth;
        const remoteVideoDisabled = room && !remoteVideoEnabled();

        return (
            <div
                ref={componentRef}
                className={
                    cx('twilio-video-container tw-bg-default',
                        {
                            'mini tw-max-w-0 tw-max-h-0 tw-overflow-visible tw-z-1': mini,
                            [miniPositionClassName]: miniPositionClassName && mini
                        })
                }
            >
                <Draggable handle='.handle' disabled={!mini} position={mini ? null : {x: 0, y: 0}}>
                    <div>
                        <ResizableBoxWrapper
                            disabled={!mini}
                            width={min([remoteVideoWidth, parentWidth]) || 0}
                            height={min([remoteVideoHeight, parentHeight]) || 0}
                            lockAspectRatio
                            {...(mini && miniConstrains)}
                        >
                            <div className={cx(
                                'tw-relative tw-w-full tw-h-full tw-overflow-hidden',
                                {'tw-flex -md:tw-flex-col tw-items-center -md:tw-justify-center': !pinParticipant}
                            )}
                            >
                                <div className={cx('handle tw-absolute tw-inset-0 tw-z-1')} />
                                <div
                                    ref={localMediaRef}
                                    id={LOCAL_MEDIA_ID}
                                    className={cx(
                                        {'pinned-media': pinParticipant === VideoPinParticipant.Local},
                                        {'unpinned-media': pinParticipant === VideoPinParticipant.Remote},
                                        {'base-media': !pinParticipant},
                                        {'tw-hidden': mini},
                                        {
                                            'full-screen-vertical': pinParticipant === VideoPinParticipant.Local &&
                                                !isLandscape && isFullScreen,
                                            'full-screen-horizontal': pinParticipant === VideoPinParticipant.Local &&
                                                isLandscape && isFullScreen,
                                        }
                                    )}
                                    onMouseMove={handleMouseMoveLocalVideo}
                                    onMouseOut={handleMouseOutLocalVideo}
                                >
                                    {localVideoOverlayVisible && overLocalVideoContent}
                                </div>
                                <div
                                    ref={remoteMediaRef}
                                    id={REMOTE_MEDIA_ID}
                                    className={cx(
                                        {'pinned-media': pinParticipant === VideoPinParticipant.Remote},
                                        {'unpinned-media': pinParticipant === VideoPinParticipant.Local},
                                        {'base-media': !pinParticipant && !mini},
                                        {
                                            'full-screen-vertical': pinParticipant === VideoPinParticipant.Remote &&
                                                !isLandscape && isFullScreen,
                                            'full-screen-horizontal': pinParticipant === VideoPinParticipant.Remote &&
                                                isLandscape && isFullScreen,
                                        })}
                                    onMouseMove={handleMouseMoveRemoteVideo}
                                    onMouseOut={handleMouseOutRemoteVideo}
                                >
                                    <Loader className='loader' svgClassName='tw-max-w-12 tw-max-h-12 screenshot-hidden' />
                                    {remoteVideoOverlayVisible && overRemoteVideoContent}
                                    {remoteVideoDisabled && <div className='tw-absolute tw-inset-0 tw-bg-dark tw-z-3' />}
                                </div>
                            </div>
                        </ResizableBoxWrapper>
                    </div>
                </Draggable>
            </div>
        );
    }
}

export default withTranslation()(TwilioVideo);
